import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/grids-package/grids.less";
import "../../stylesheets/mobile-package/mobile.less";
import "../../stylesheets/odometer-package/odometer.less";
import "../../stylesheets/profile-package/profile.less";
import "../../stylesheets/profile-nav-package/profile-nav.less";

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import Link from "@khanacademy/wonder-blocks-link";
import {isUsernameValid} from "@khan/auth-password";
import Log, {Errors} from "@khan/logging";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import UserCardView from "#profile-nav-package/user-card-view.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import Error from "./content-pages/error.tsx";
import {
    getProfileModel,
    isKaidValid,
    profilePathIsMine,
} from "./content-pages/profile-util.ts";

type OwnProps = {
    // NOTE: this is misleading as it's really a profile identifier (kaid or
    // a username). It's not guaranteed to be a kaid.
    profileKaid: string | null | undefined;
    isActivityAccessible: boolean;
    userNotFound?: boolean;
};

type Props = OwnProps & // We inject the profile for the user here because if the heading
    // being shown is for the current user, then we want to show the
    // injected/updated profile.  We'll still fetch the profile from
    // the profileKaid (above), but we'd like to responde to updates
    // that occur on the current user.
    WithUserProfileProps &
    WithLocationProps &
    WithApolloProps;

type State = {
    // The heading can be shown for users other than the current user,
    // so we need to load it from the KAID passed in.  We might use the
    // injected profile, if the currently viewed profile is our own.
    loadedProfileModelFromKaid?: ProfileModelType;
    showError: boolean;
};

class Heading extends React.Component<Props, State> {
    // @ts-expect-error - TS2564 - Property '_isMounted' has no initializer and is not definitely assigned in the constructor.
    _isMounted: boolean;

    state: State = {
        loadedProfileModelFromKaid: undefined,
        showError: false,
    };

    componentDidMount() {
        this.loadProfileModelFromKaid();
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.profileKaid !== prevProps.profileKaid) {
            this.loadProfileModelFromKaid();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    loadProfileModelFromKaid() {
        const {profileKaid, gqlClient} = this.props;
        this._isMounted = true;

        getProfileModel(gqlClient, profileKaid || "")
            .then((profileModel) => {
                if (profileModel && this._isMounted) {
                    this.setState({loadedProfileModelFromKaid: profileModel});
                }
            })
            .catch((error) => {
                Log.error("Error initializing LIHP Heading:", Errors.Internal, {
                    cause: error,
                });
                if (this._isMounted) {
                    this.setState({showError: true});
                }
            });
    }

    getProfileModel(): ProfileModelType | null | undefined {
        const {profile} = this.props;
        const {loadedProfileModelFromKaid} = this.state;

        // This can be a bit confusing, so let me explain: We have two
        // profiles we're keeping track of.  One was injected as the user's
        // profile and the other is the one we load from KAID.  Since this
        // component can be used for users other than the logged-in user,
        // we can't rely on the profile being correct.  BUT, we also need
        // to be notified if the logged-in user's profile changes.  So we
        // check here.  If we have both and the KAIDs are the same, use the
        // injected version.  It is up-to-date with the data in the apollo
        // cache.
        if (
            profile &&
            loadedProfileModelFromKaid &&
            profile.get("kaid") === loadedProfileModelFromKaid.get("kaid")
        ) {
            return profile;
        }

        return loadedProfileModelFromKaid;
    }

    render(): React.ReactNode {
        const {isActivityAccessible, userNotFound, profileKaid, location} =
            this.props;
        const {showError} = this.state;

        const profileModel = this.getProfileModel();

        if (userNotFound) {
            const isValidId =
                profileKaid &&
                (isKaidValid(profileKaid) ||
                    isUsernameValid(profileKaid).result);

            // If the profile identifier provided is not a kaid or a valid
            // username, it's likely that we failed to get their profile data
            // due to a faulty username.
            if (profilePathIsMine(profileModel, location)) {
                const body = isValidId ? (
                    t`Oops. It looks like that profile doesn't exist.`
                ) : (
                    <Trans>
                        Oops. We’ve encountered an issue with your profile.
                        Please choose a new username from the{" "}
                        <Link href="/settings/account">Settings page</Link>
                    </Trans>
                );

                return <Error body={body} />;
            }

            return (
                <Error
                    body={t`Oops. It looks like that profile doesn't exist.`}
                />
            );
        }

        if (showError) {
            return <Error />;
        }

        if (!profileModel) {
            return null;
        }

        // TODO(BrianG): Remove this zIndex change and update the Streaks
        // popover to use the WonderBlocks popover.  This zIndex change
        // won't be necessary anymore.
        return (
            <View id="nav-container" style={styles.elevate}>
                <div className="lihp-heading">
                    <UserCardView
                        model={profileModel}
                        showBadgeSummary={isActivityAccessible}
                    />
                </div>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    elevate: {
        zIndex: 1,
    },
});

type ExportProps = WithoutApollo<
    WithoutUserProfile<
        WithoutLocation<
            JSX.LibraryManagedAttributes<
                typeof Heading,
                React.ComponentProps<typeof Heading>
            >
        >
    >
>;
export default compose(
    withUserProfile,
    withLocation,
    withApollo,
)(Heading) as React.ComponentType<ExportProps>;
